import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@safetyhub/ui';
import { adminApi } from '../lib/api';
import FlagList from '../components/feature-flags/FlagList';
import FlagCreateModal from '../components/feature-flags/FlagCreateModal';
import FlagDetailModal from '../components/feature-flags/FlagDetailModal';

type StatusFilter = 'ACTIVE' | 'ARCHIVED';

export default function FeatureFlags() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ACTIVE');
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedFlagId, setSelectedFlagId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'feature-flags', statusFilter],
    queryFn: () =>
      adminApi.getFeatureFlags({ status: statusFilter }),
  });

  const filteredFlags = useMemo(() => {
    const flags = data?.data || [];
    if (!search.trim()) return flags;
    const q = search.toLowerCase();
    return flags.filter(
      (f) =>
        f.key.toLowerCase().includes(q) ||
        f.name.toLowerCase().includes(q) ||
        f.tags.some((t) => t.toLowerCase().includes(q)),
    );
  }, [data?.data, search]);

  const statusTabs: { key: StatusFilter; label: string }[] = [
    { key: 'ACTIVE', label: 'Active' },
    { key: 'ARCHIVED', label: 'Archived' },
  ];

  return (
    <div>
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">피처 플래그</h2>
          <p className="mt-1 text-sm text-gray-500">
            기능 플래그를 관리하고 점진적 배포를 제어합니다.
          </p>
        </div>
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>
          <span>+</span>
          <span>새 플래그</span>
        </Button>
      </div>

      {/* 상태 탭 */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="-mb-px flex gap-6">
          {statusTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                statusFilter === tab.key
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* 검색 */}
      <div className="mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Key, Name, Tag로 검색..."
          className="w-full max-w-sm px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
        />
      </div>

      {/* 콘텐츠 */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      ) : error || !data?.success ? (
        <div className="bg-red-50 p-4 rounded text-red-600">
          데이터를 불러오는데 실패했습니다.
        </div>
      ) : (
        <>
          <FlagList flags={filteredFlags} onSelect={setSelectedFlagId} />
          <p className="mt-4 text-sm text-gray-500">
            총 {filteredFlags.length}개
            {search && ` (검색 결과)`}
          </p>
        </>
      )}

      {/* 생성 모달 */}
      {showCreateModal && (
        <FlagCreateModal onClose={() => setShowCreateModal(false)} />
      )}

      {/* 상세 모달 */}
      {selectedFlagId && (
        <FlagDetailModal
          flagId={selectedFlagId}
          onClose={() => setSelectedFlagId(null)}
        />
      )}
    </div>
  );
}
